import type {
  OddPlayerPolicy,
  PairingPriority,
  PlayEvent,
  PlayEventFormat,
  PlayEventParticipant,
  PlayEventSide,
  PlayEventStatus,
  PlayGenderRestriction,
  PlayJoinPolicy,
  PlayParticipantStatus,
  PlayPaymentMode,
  PlayPaymentStatus,
  SocialPlayMode,
} from '@court-sync/shared';
import { resolvePlayEventPrice } from '@court-sync/shared';

export const PLAY_EVENT_STATUS_LABELS: Record<PlayEventStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  check_in_open: 'Check-in open',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const PLAY_EVENT_STATUS_BADGE_VARIANT: Record<
  PlayEventStatus,
  'success' | 'warning' | 'secondary' | 'destructive' | 'default'
> = {
  draft: 'secondary',
  published: 'default',
  check_in_open: 'warning',
  in_progress: 'success',
  completed: 'secondary',
  cancelled: 'destructive',
};

/** Customer-facing status copy for public play event surfaces (omit internal workflow labels). */
export function getPublicPlayEventStatusLabel(status: PlayEventStatus): string | null {
  switch (status) {
    case 'published':
      return null;
    case 'check_in_open':
      return 'Check-in open';
    case 'in_progress':
      return 'In progress';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return null;
  }
}

export function getPublicPlayEventStatusBadgeVariant(
  status: PlayEventStatus
): 'success' | 'warning' | 'secondary' | 'destructive' | 'default' | null {
  if (status === 'published' || status === 'draft') {
    return null;
  }
  return PLAY_EVENT_STATUS_BADGE_VARIANT[status];
}

export const PLAY_EVENT_FORMAT_LABELS: Record<PlayEventFormat, string> = {
  social_rotate: 'Social rotate',
  round_robin: 'Round robin',
  single_elimination: 'Single elimination',
};

export const PLAY_GENDER_RESTRICTION_LABELS: Record<PlayGenderRestriction, string> = {
  open: 'Open (any gender)',
  male: 'Men only',
  female: 'Women only',
};

export const PLAY_EVENT_SIDE_LABELS: Record<PlayEventSide, string> = {
  singles: 'Singles',
  doubles: 'Doubles',
  mixed: 'Mixed',
};

export const PLAY_JOIN_POLICY_LABELS: Record<PlayJoinPolicy, string> = {
  open: 'Open',
  members_only: 'Members only',
  students_only: 'Students only',
  members_and_students: 'Members & students',
};

export const PLAY_PAYMENT_MODE_LABELS: Record<PlayPaymentMode, string> = {
  online_on_join: 'Online on join',
  at_door: 'At door',
};

export const PLAY_PARTICIPANT_STATUS_LABELS: Record<PlayParticipantStatus, string> = {
  registered: 'Registered',
  checked_in: 'Checked in',
  withdrawn: 'Withdrawn',
  no_show: 'No show',
};

export const PLAY_PARTICIPANT_STATUS_BADGE_VARIANT: Record<
  PlayParticipantStatus,
  'success' | 'warning' | 'secondary' | 'destructive' | 'default'
> = {
  registered: 'default',
  checked_in: 'success',
  withdrawn: 'secondary',
  no_show: 'destructive',
};

export const PLAY_PAYMENT_STATUS_LABELS: Record<PlayPaymentStatus, string> = {
  unpaid: 'Unpaid',
  paid: 'Paid',
  comped: 'Complimentary',
  waived: 'Waived',
};

export const PLAY_PAYMENT_STATUS_BADGE_VARIANT: Record<
  PlayPaymentStatus,
  'success' | 'warning' | 'secondary' | 'default'
> = {
  unpaid: 'warning',
  paid: 'success',
  comped: 'secondary',
  waived: 'secondary',
};

type PlayParticipantPaymentContext = Pick<
  PlayEvent,
  'memberPriceCents' | 'studentPriceCents' | 'guestPriceCents' | 'paymentMode'
>;
type PlayParticipantPaymentFields = Pick<
  PlayEventParticipant,
  'participantType' | 'paymentStatus'
>;

function isPlayParticipantNoCharge(
  event: PlayParticipantPaymentContext,
  participant: PlayParticipantPaymentFields
): boolean {
  if (participant.paymentStatus !== 'unpaid') {
    return false;
  }
  const price = resolvePlayEventPrice({
    event,
    participantType: participant.participantType,
  });
  return price.finalPriceCents === 0;
}

export function getPlayParticipantPaymentLabel(
  event: PlayParticipantPaymentContext,
  participant: PlayParticipantPaymentFields
): string {
  if (isPlayParticipantNoCharge(event, participant)) {
    return 'No charge';
  }
  return PLAY_PAYMENT_STATUS_LABELS[participant.paymentStatus];
}

export function getPlayParticipantPaymentBadgeVariant(
  event: PlayParticipantPaymentContext,
  participant: PlayParticipantPaymentFields
): 'success' | 'warning' | 'secondary' | 'default' {
  if (isPlayParticipantNoCharge(event, participant)) {
    return 'secondary';
  }
  return PLAY_PAYMENT_STATUS_BADGE_VARIANT[participant.paymentStatus];
}

export const SOCIAL_PLAY_MODE_LABELS: Record<SocialPlayMode, string> = {
  singles_only: 'Singles only',
  doubles_only: 'Doubles only',
  prefer_doubles: 'Prefer doubles',
};

export const ODD_PLAYER_POLICY_LABELS: Record<OddPlayerPolicy, string> = {
  sit_out: 'Sit out',
  fallback_singles: 'Fallback singles',
  american_singles: 'American singles',
  fallback_doubles_sit_out: 'Doubles sit out',
};

export const PAIRING_PRIORITY_LABELS: Record<PairingPriority, string> = {
  mixed_gender: 'Mixed gender pairs',
  skill_balance: 'Balance skill levels',
  player_rating_balance: 'Balance player ratings',
  age_balance: 'Balance ages',
  minimize_repeat_partners: 'Minimize repeat partners',
};
